import React from "react";
import Reveal from "./Reveal";
import { TEAM, STATS } from "../mock/mock";
import { Star, Squiggle, CircleOutline } from "./Decor";
import { XIcon } from "./SocialIcons";
import { Infinity as InfinityIcon } from "lucide-react";

const STAT_STYLES = [
  { bg: "bg-teal-500", rot: "-rotate-2" },
  { bg: "bg-orange-400", rot: "rotate-2" },
  { bg: "bg-cyan-500", rot: "rotate-1" },
  { bg: "bg-indigo-500", rot: "-rotate-1" },
];

export default function About() {
  return (
    <section id="about" className="relative overflow-hidden bg-[#f5fbfc] dark:bg-slate-950 dots-bg py-24 sm:py-32 transition-colors">
      {/* artistic floating accents */}
      <Star className="absolute top-16 right-[12%] w-10 h-10 text-orange-300 dark:text-orange-400/40 animate-deco" />
      <CircleOutline className="absolute bottom-24 left-[6%] w-16 h-16 text-cyan-300 dark:text-cyan-500/30 animate-deco" />
      <Squiggle className="absolute top-1/2 right-[4%] w-24 text-rose-300 dark:text-rose-400/30 animate-deco" />

      <div className="relative max-w-7xl mx-auto px-6">
        <Reveal>
          <p className="text-cyan-600 dark:text-cyan-400 font-bold tracking-widest uppercase text-sm mb-3">About</p>
          <h2 className="font-display font-800 text-4xl sm:text-5xl lg:text-6xl text-slate-900 dark:text-white max-w-3xl leading-tight">
            No sharks are harmed during <span className="text-gradient">development</span>.
          </h2>
          <p className="mt-6 text-lg text-slate-600 dark:text-slate-400 max-w-2xl">
            Our mission is simple; craft vibrant, joyful video games that give our players
            real <span className="marker-hi font-semibold text-slate-800 dark:text-white">joy and immersion</span>. To put it simply, we make games for you.
          </p>
        </Reveal>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
          {STATS.map((s, i) => {
            const st = STAT_STYLES[i % STAT_STYLES.length];
            return (
              <Reveal key={s.label} delay={i * 80}>
                <div
                  className={`${st.bg} ${st.rot} rounded-3xl p-6 text-white sticker hover:rotate-0 hover:-translate-y-1.5 transition-transform`}
                >
                  <div className="font-display font-800 text-4xl leading-none h-10 flex items-center">
                    {s.value === "\u221E" ? <InfinityIcon className="h-9 w-auto" strokeWidth={3} /> : s.value}
                  </div>
                  <div className="text-white/90 font-semibold mt-1">{s.label}</div>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Team */}
        <div className="mt-24">
          <Reveal>
            <h3 className="font-display font-700 text-2xl sm:text-3xl text-slate-900 dark:text-white mb-10">
              The humans behind the bowl
            </h3>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-8 items-stretch">
            {TEAM.map((m, i) => (
              <Reveal key={m.name} delay={i * 120} className="h-full">
                <div className={`tilt-card group relative h-full flex flex-col rounded-[2rem] overflow-hidden bg-white dark:bg-slate-900 border-4 ${i % 2 === 0 ? "border-teal-400 dark:border-teal-500/40" : "border-orange-300 dark:border-orange-400/40"}`}>
                  <div className="aspect-[4/3] overflow-hidden shrink-0">
                    <img
                      src={m.img}
                      alt={m.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h4 className="font-display font-700 text-xl text-slate-900 dark:text-white">{m.name}</h4>
                    <p className="text-cyan-600 dark:text-cyan-400 font-semibold text-sm mt-0.5">{m.role}</p>
                    <p className="text-slate-500 dark:text-slate-400 mt-3 flex-1">{m.bio}</p>
                    {m.twitter && (
                      <a
                        href={m.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${m.name} on X`}
                        className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-slate-500 dark:text-slate-300 hover:text-teal-600 dark:hover:text-cyan-300 transition-colors"
                      >
                        <XIcon className="w-4 h-4" /> Follow
                      </a>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}